/**
 * gyroidPipeline — 3D raymarched triply-periodic minimal surface.
 *
 * The gyroid is a famous mathematical surface: sin(x)*cos(y) + sin(y)*cos(z)
 * + sin(z)*cos(x) = 0. It's a triply-periodic minimal surface with no
 * self-intersections. We render it with iridescent lighting based on
 * the surface normal and viewing angle.
 */
import { linkProgram, POST_ATTRIBS } from "./glContext";

export interface GyroidPipeline {
  program: WebGLProgram;
  vao: WebGLVertexArrayObject | null;
  vbo: WebGLBuffer | null;
  uResolution: WebGLUniformLocation | null;
  uTime: WebGLUniformLocation | null;
  uBass: WebGLUniformLocation | null;
  uMid: WebGLUniformLocation | null;
  uHigh: WebGLUniformLocation | null;
  bands: Float32Array;
}

const VERT_SRC = `#version 300 es
precision highp float;
layout(location = 0) in vec2 aPos;
out vec2 vUV;
void main() {
  vUV = aPos * 0.5 + 0.5;
  gl_Position = vec4(aPos, 0.0, 1.0);
}`;

const FRAG_SRC = `#version 300 es
precision highp float;
in vec2 vUV;
out vec4 outColor;

uniform vec2 uResolution;
uniform float uTime;
uniform vec3 uBass;
uniform vec3 uMid;
uniform vec3 uHigh;

float bass;
float mid;
float high;

// Gyroid level set: F(x,y,z) = sin(x)*cos(y) + sin(y)*cos(z) + sin(z)*cos(x).
// We use the F/|grad F| approximation as a pseudo-SDF.
float gyroidLevel(vec3 p) {
  float scale = 3.0 + bass * 0.5;
  vec3 q = p * scale;
  return sin(q.x) * cos(q.y) + sin(q.y) * cos(q.z) + sin(q.z) * cos(q.x);
}

vec3 calcNormal(vec3 p) {
  float eps = 0.01;
  return normalize(vec3(
    gyroidLevel(p + vec3(eps, 0.0, 0.0)) - gyroidLevel(p - vec3(eps, 0.0, 0.0)),
    gyroidLevel(p + vec3(0.0, eps, 0.0)) - gyroidLevel(p - vec3(0.0, eps, 0.0)),
    gyroidLevel(p + vec3(0.0, 0.0, eps)) - gyroidLevel(p - vec3(0.0, 0.0, eps))
  ));
}

void main() {
  // Audio bands.
  bass = (uBass.x + uBass.y + uBass.z) / 3.0;
  mid = (uMid.x + uMid.y + uMid.z) / 3.0;
  high = (uHigh.x + uHigh.y + uHigh.z) / 3.0;
  bass = pow(clamp(bass * 2.5, 0.0, 1.0), 0.7);
  mid = pow(clamp(mid * 3.0, 0.0, 1.0), 0.7);
  high = pow(clamp(high * 4.0, 0.0, 1.0), 0.7);

  // Aspect-corrected coords.
  vec2 p = (gl_FragCoord.xy - uResolution * 0.5) / min(uResolution.x, uResolution.y);
  p.x *= uResolution.x / uResolution.y;
  // Gyroid shader: visualize the triply-periodic minimal surface as
  // a 2D projection. For each screen pixel, we sample the gyroid
  // function on a Z-slab and accumulate density. This gives a
  // beautiful "tissue" of the surface.

  // Time-varying parameters.
  float t = uTime * 0.3;
  // Slow rotation of the gyroid coordinate frame.
  float angle = t;
  float ca = cos(angle), sa = sin(angle);
  mat2 rot = mat2(ca, -sa, sa, ca);
  vec2 rp = rot * p;
  // 3D position in gyroid space.
  // For each screen pixel, sample the gyroid function for several Z values
  // and accumulate. This gives a "density" visualization.
  vec3 col = vec3(0.04, 0.03, 0.08);
  float density = 0.0;
  for (int zi = 0; zi < 24; zi++) {
    float z = (float(zi) / 24.0 - 0.5) * 4.0 + t * 0.7;
    vec3 q = vec3(rp * 2.5, z * 2.5);
    // Gyroid level set.
    float v = sin(q.x) * cos(q.y) + sin(q.y) * cos(q.z) + sin(q.z) * cos(q.x);
    // Density: high when close to the level set.
    float d = exp(-abs(v) * 6.0) * (0.6 + bass * 0.4);
    // Color based on Z and audio.
    vec3 layerColor = mix(
      vec3(0.30, 0.50, 0.95),
      vec3(0.95, 0.40, 0.85),
      float(zi) / 24.0
    );
    col += layerColor * d * 0.2;
    density += d;
  }
  // Bright on the surface.
  col *= 1.0 + mid * 0.4;
  // Normalize by depth.
  col = col / (1.0 + density * 0.1);
  // Audio: pulse.
  col *= 1.0 + bass * 0.2;

  // Vignette.
  float vig = 1.0 - dot(p, p) * 0.3;
  col *= clamp(vig, 0.0, 1.0);

  // Tone mapping.
  col = (col * (2.51 * col + 0.03)) / (col * (2.43 * col + 0.59) + 0.14);
  col = pow(clamp(col, 0.0, 1.0), vec3(1.0 / 2.2));

  outColor = vec4(col, 1.0);
}
`;

export function computeGyroidBands(t: number, out: Float32Array): void {
  out[0] = 0.5 + 0.3 * Math.sin(t * 0.5);
  out[1] = 0.5 + 0.3 * Math.sin(t * 0.7 + 1.0);
  out[2] = 0.4 + 0.3 * Math.sin(t * 1.0 + 2.0);
  out[3] = 0.5 + 0.3 * Math.sin(t * 1.2 + 1.5);
  out[4] = 0.5 + 0.3 * Math.sin(t * 1.4 + 0.5);
  out[5] = 0.4 + 0.3 * Math.sin(t * 1.7 + 2.5);
  out[6] = 0.3 + 0.4 * Math.sin(t * 2.1 + 0.7);
  out[7] = 0.4 + 0.3 * Math.sin(t * 2.5 + 1.8);
  out[8] = 0.3 + 0.4 * Math.sin(t * 3.0 + 3.0);
}

export function computeGyroidRealtimeBands(
  freqData: Uint8Array,
  out: Float32Array,
): void {
  const N = freqData.length;
  const ranges = [
    [0.00, 0.030], [0.030, 0.080], [0.080, 0.150],
    [0.150, 0.260], [0.260, 0.380], [0.380, 0.500],
    [0.500, 0.660], [0.660, 0.820], [0.820, 0.980],
  ];
  for (let i = 0; i < 9; i++) {
    const [lo, hi] = ranges[i];
    const start = Math.floor(lo * N);
    const end = Math.floor(hi * N);
    let sum = 0, count = 0;
    for (let k = start; k < end; k++) {
      sum += freqData[k];
      count++;
    }
    out[i] = count > 0 ? (sum / count) / 255 : 0;
  }
}

export function createGyroidPipeline(gl: WebGL2RenderingContext): GyroidPipeline | null {
  const program = linkProgram(gl, VERT_SRC, FRAG_SRC, POST_ATTRIBS);
  if (!program) return null;
  const vao = gl.createVertexArray();
  const vbo = gl.createBuffer();
  if (!vao || !vbo) return null;
  const verts = new Float32Array([
    -1, -1,  1, -1,  -1, 1,
    -1,  1,  1, -1,   1, 1,
  ]);
  gl.bindVertexArray(vao);
  gl.bindBuffer(gl.ARRAY_BUFFER, vbo);
  gl.bufferData(gl.ARRAY_BUFFER, verts, gl.STATIC_DRAW);
  gl.enableVertexAttribArray(POST_ATTRIBS.aPos);
  gl.vertexAttribPointer(POST_ATTRIBS.aPos, 2, gl.FLOAT, false, 0, 0);
  gl.bindVertexArray(null);
  return {
    program, vao, vbo,
    uResolution: gl.getUniformLocation(program, "uResolution"),
    uTime: gl.getUniformLocation(program, "uTime"),
    uBass: gl.getUniformLocation(program, "uBass"),
    uMid: gl.getUniformLocation(program, "uMid"),
    uHigh: gl.getUniformLocation(program, "uHigh"),
    bands: new Float32Array(9),
  };
}

export function drawGyroid(
  gl: WebGL2RenderingContext,
  pipe: GyroidPipeline,
  width: number,
  height: number,
  t: number,
): void {
  gl.useProgram(pipe.program);
  gl.uniform2f(gl.getUniformLocation(pipe.program, "uResolution"), width, height);
  gl.uniform1f(gl.getUniformLocation(pipe.program, "uTime"), t);
  gl.uniform3f(gl.getUniformLocation(pipe.program, "uBass"), pipe.bands[0], pipe.bands[1], pipe.bands[2]);
  gl.uniform3f(gl.getUniformLocation(pipe.program, "uMid"), pipe.bands[3], pipe.bands[4], pipe.bands[5]);
  gl.uniform3f(gl.getUniformLocation(pipe.program, "uHigh"), pipe.bands[6], pipe.bands[7], pipe.bands[8]);
  gl.bindVertexArray(pipe.vao);
  gl.drawArrays(gl.TRIANGLES, 0, 6);
  gl.bindVertexArray(null);
}
